/**
 * Ключ модуля реестра → страница кабинета.
 *
 * У части модулей есть своя страница с формой под конкретный источник и
 * разбором ответа — на неё и надо вести. У остальных страницы нет, и до сих
 * пор это значило «в интерфейсе модуля не существует»: боковое меню — ручной
 * список российских проверок, а карточки каталога никуда не вели. Ключ без
 * своей страницы открывается универсальной страницей модуля по адресу
 * `/modules/<полный ключ>`.
 *
 * Список ведётся руками — другого способа нет, страницы написаны под
 * конкретные источники. Зато ошибка в нём не прячется: ключ, которого нет в
 * реестре, открывает страницу с прямым текстом «модуль не найден», а не
 * пустой экран.
 */

/** Модули, у которых есть выделенная страница (ключ страницы в App.tsx). */
const MODULE_PAGE: Readonly<Record<string, string>> = {
  // Валидация
  "validation.rus.address": "address_validation",
  "validation.global.phone": "phone_validation",
  "validation.global.email": "email_validation",
  "validation.global.ip_address": "ip_address_validation",
  "validation.rus.name": "name_validation",
  "validation.rus.document": "document_validation",
  "validation.rus.tax_id": "tax_id_validation",
  "validation.rus.social_security_number": "snils_validation",

  // Верификация
  "verification.rus.fns_tax_id": "fns_tax_id_verification",
  "verification.rus.mvd_passport": "passport_verification",
  "verification.rus.mvd_driver_license": "mvd_driver_license_verification",
  "verification.rus.sfr_ssn": "sfr_ssn_verification",
  "verification.rus.obrnadzor_education": "obrnadzor_education_verification",

  // Административные нарушения
  "compliance.rus.fns_disqualified": "admin_disqualified",
  "compliance.rus.fns_au_violations": "au_violations",

  // Криминал и розыск
  "compliance.rus.mvd_wanted": "mvd_wanted",
  "compliance.rus.fssp_criminal_wanted": "criminal_wanted",
  "compliance.rus.fssp_debtor_wanted": "debtor_wanted",
  "compliance.rus.fsin_criminal_wanted": "fsin_criminal_wanted",
  "compliance.global.interpol_red_notice": "interpol_red_notice",
  "compliance.global.interpol_yellow_notice": "interpol_yellow_notice",
  "compliance.global.interpol_un_sanctions": "interpol_un_sanctions",

  // Долги и залоги
  "compliance.rus.fssp_ip_debtor": "execution_proceedings",
  "compliance.rus.fssp_alimony_debtor": "alimony_debt",
  "compliance.rus.fns_tax_debtor": "tax_debt",
  "compliance.rus.fns_account_suspension": "account_suspension",
  "compliance.rus.credit_scoring": "credit_scoring",
  "compliance.rus.credit_rating": "credit_rating",
  "compliance.rus.fnp_pledges": "pledge",

  // Суды и банкротство
  "compliance.rus.general_courts": "general_courts",
  "compliance.rus.arbitration_courts": "arbitration_courts",
  "compliance.rus.efrsb_bankruptcy": "bankruptcy",
  "compliance.rus.fns_subsidiary_defendant": "fns_subsidiary_defendant",

  // Негативные реестры
  "compliance.global.sanctions_screening": "sanctions_screening",
  "compliance.global.pep_screening": "pep_screening",
  "compliance.rus.fsfm_terrorists": "terrorist_extreme",
  "compliance.rus.minjust_foreign_agents": "foreign_agent",
  "compliance.rus.government_corruptioners": "corruption",
  "compliance.rus.cbr_warning_list_fl": "cbr_warning_list",
  "compliance.rus.fns_mass_leaders": "fns_mass_leaders",
  "compliance.rus.fns_mass_founders": "fns_mass_founders",

  // Миграция
  "compliance.rus.mvd_controlled_persons": "mvd_controlled_persons",

  // Цифровой след и сводные сценарии
  "osint.rus.enrich_contact": "digital_footprint",
  "business.rus.legal_report": "company_report",
  "report.rus.complex_person": "complex_check",

  // Криптоадреса: проверка и трассировка — одна страница
  "crypto.global.address_screen": "crypto_check",
  "crypto.global.trace": "crypto_check",
  "crypto.global.tx_risk": "crypto_check",
};

/** Есть ли у модуля выделенная страница. */
export function hasDedicatedPage(moduleKey: string): boolean {
  return moduleKey in MODULE_PAGE;
}

/**
 * Ключ страницы для модуля: выделенная страница, если она есть, иначе сам
 * ключ модуля — универсальная страница живёт по `/modules/<ключ>`.
 */
export function pageForModule(moduleKey: string): string {
  return MODULE_PAGE[moduleKey] ?? moduleKey;
}

/** Обратное соответствие: модуль, который открывает эта страница. */
export function moduleForPage(pageKey: string): string | null {
  const found = Object.entries(MODULE_PAGE).find(([, page]) => page === pageKey);
  return found ? found[0] : null;
}

export { MODULE_PAGE };
